import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react';

import { cn } from '@shared/lib/cn';
import { Spinner } from '@shared/ui/spinner';

import {
  SIZE_CLASS,
  SPINNER_SIZE,
  TYPO_CLASS,
  TYPO_STRONG_CLASS,
  VARIANT_CLASS,
} from './button.constant';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'danger'
  | 'danger-outline'
  | 'neutral'
  | 'tonal-primary'
  | 'tonal-danger'
  | 'tonal-neutral'
  | 'inverse'
  | 'primary-pill';

export type ButtonSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg';

export type ButtonStatus = 'default' | 'loading';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Figma `variant`. 기본 primary */
  variant?: ButtonVariant;
  /** Figma `size`(높이 2xs 24 · xs 28 · sm 32 · md 40 · lg 48). 기본 xs */
  size?: ButtonSize;
  /** Figma `status`. loading 이면 Spinner 를 겹치고 누를 수 없다(`aria-busy`) */
  status?: ButtonStatus;
  /** Figma `Leading icon` + `Icon`. 글자 앞 아이콘(`<Icon name="…" size="sm" />`) */
  leadingIcon?: ReactNode;
  /** Figma `Label` */
  children?: ReactNode;
  /** 고정 너비는 부르는 쪽이 준다: `className="w-(--size-button-md)"` */
  className?: string;
}

const TONAL: ReadonlySet<ButtonVariant> = new Set([
  'tonal-primary',
  'tonal-danger',
  'tonal-neutral',
]);

/**
 * 누르면 동작을 실행한다. 너비는 글자에 맞추고(Figma HUG), 고정 너비는 `className` 으로 준다.
 * hover · pressed 는 CSS 가상 클래스, 포커스 링은 전역 `:focus-visible` 규칙(fe-rules 2절).
 */
export function Button({
  variant = 'primary',
  size = 'xs',
  status = 'default',
  leadingIcon,
  children,
  className,
  type = 'button',
  onClick,
  ...rest
}: ButtonProps) {
  const loading = status === 'loading';

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    if (loading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  }

  return (
    <button
      type={type}
      data-size={size}
      data-status={status}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={handleClick}
      className={cn(
        'relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap select-none disabled:cursor-not-allowed',
        VARIANT_CLASS[variant],
        SIZE_CLASS[size],
        TONAL.has(variant) ? TYPO_STRONG_CLASS[size] : TYPO_CLASS[size],
        loading && 'pointer-events-none',
        className,
      )}
      {...rest}
    >
      {leadingIcon ? (
        <span
          data-slot="leading-icon"
          className={cn('inline-flex shrink-0', loading && 'opacity-(--opacity-disabled)')}
        >
          {leadingIcon}
        </span>
      ) : null}
      {children !== undefined && children !== null ? (
        <span data-slot="label" className={cn(loading && 'opacity-(--opacity-disabled)')}>
          {children}
        </span>
      ) : null}
      {loading ? (
        <Spinner
          data-slot="spinner"
          aria-hidden
          size={SPINNER_SIZE[size]}
          tone="primary"
          className="absolute inset-0 m-auto"
        />
      ) : null}
    </button>
  );
}
